import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, openingHoursSchema, UpcomingChanges } from "../useBusinessInfo";
import "./contact.css";

/**
 * Contact — redesigned public page (route: /contact).
 *
 * Functional ground truth: frontend/src/pages/ContactPage.js — SEO head +
 * LocalBusiness JSON-LD, tel:/mailto: contact links, static directions &
 * visit info, and a contact-form submit handler that SIMULATES sending
 * (1s delay → success, reset fields; there is no /api/contact endpoint —
 * replicated exactly, per the handoff spec).
 * Visual ground truth: design-ref "Contact.html".
 */

const baseSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Umoona Opal Mine & Museum",
  description:
    "Contact Umoona Opal Mine & Museum in Coober Pedy. Get directions, hours, phone number and email. Book underground mine tours or inquire about authentic opals.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "14 Hutchison St",
    addressLocality: "Coober Pedy",
    addressRegion: "SA",
    postalCode: "5723",
    addressCountry: "AU",
  },
  telephone: "+61886725288",
  url: "https://umoonaopalmine.com.au/contact",
};

export default function RContactPage() {
  const hours = useOpeningHours();
  const schemaData = { ...baseSchema, ...openingHoursSchema(hours) };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Parity with the legacy ContactPage: the submit is simulated — there
      // is no contact backend endpoint. Real contact is via phone/email.
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError("Failed to send message. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingShell pageClass="pg-contact">
      <SEOHead
        title="Contact Us - Visit Umoona Opal Mine & Museum"
        description="Contact Umoona Opal Mine & Museum in Coober Pedy. Get directions, hours, phone number and email. Book underground mine tours or inquire about authentic opals."
        keywords="contact Umoona, Coober Pedy contact, opal mine contact, underground tours booking, museum contact"
        canonical="/contact"
        schemaData={schemaData}
      />

      {/* HERO */}
      <div className="u-hero">
        <img
          className="u-hero__img"
          src="/design/assets/contact-hero.jpg"
          alt="Contact Umoona Opal Mine & Museum"
        />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">We're here to help</div>
          <h1 className="u-hero__title">Contact Umoona</h1>
          <p className="u-hero__sub">
            Planning your visit to Coober Pedy? Have questions about tours,
            accommodation or opals? We're here to make your underground
            experience unforgettable.
          </p>
        </div>
      </div>

      {/* CONTACT CARDS + FORM + DIRECTIONS */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-grid3 ct-cards">
          <a href="tel:(08) 8672 5288" className="u-card ct-card">
            <div className="u-card__kicker">CALL US</div>
            <div className="ct-card__big">(08) 8672 5288</div>
            <p className="u-card__text">{hours.summary.join(". ")}.</p>
            <UpcomingChanges
              specialDays={hours.special_days}
              className="u-card__text ct-card__changes"
            />
          </a>
          <a
            href="mailto:admin@umoonaopalmine.com.au"
            className="u-card ct-card"
          >
            <div className="u-card__kicker ct-kick--teal">EMAIL</div>
            <div className="ct-card__big">admin@umoonaopalmine.com.au</div>
            <p className="u-card__text">We'll get back to you promptly.</p>
          </a>
          <div className="u-card ct-card">
            <div className="u-card__kicker ct-kick--turq">VISIT</div>
            <div className="ct-card__big">14 Hutchison St</div>
            <p className="u-card__text">Coober Pedy SA 5723, Australia.</p>
          </div>
        </div>

        <div className="u-split ct-split">
          {/* Contact form */}
          <form className="ct-formcard" onSubmit={handleSubmit}>
            <div className="ct-formtitle">Send us a message</div>

            {success && (
              <div className="ct-note ct-note--success" role="status">
                Thanks for your message! We'll get back to you promptly.
              </div>
            )}
            {error && (
              <div className="ct-note ct-note--error" role="alert">
                {error}
              </div>
            )}

            <label className="ct-field">
              <span className="ct-label">Name *</span>
              <input
                className="ct-input"
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange("name")}
                required
              />
            </label>
            <label className="ct-field">
              <span className="ct-label">Email *</span>
              <input
                className="ct-input"
                type="email"
                placeholder="you@email.com"
                value={formData.email}
                onChange={handleChange("email")}
                required
              />
            </label>
            <label className="ct-field">
              <span className="ct-label">Phone</span>
              <input
                className="ct-input"
                type="tel"
                placeholder="Phone (optional)"
                value={formData.phone}
                onChange={handleChange("phone")}
              />
            </label>
            <label className="ct-field ct-field--last">
              <span className="ct-label">Message *</span>
              <textarea
                className="ct-textarea"
                rows={4}
                placeholder="How can we help?"
                value={formData.message}
                onChange={handleChange("message")}
                required
              />
            </label>
            <button className="ct-submit" type="submit" disabled={loading}>
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>

          {/* Directions & visit info */}
          <div>
            <div className="ct-infocard">
              <div className="ct-infotitle">Find us in Coober Pedy</div>
              <p className="ct-infotext">
                Right on Hutchison Street, the main road through Coober Pedy.
                Look for our distinctive signage and the underground entrance.
              </p>
              <div className="ct-infolist">
                · 846 km north of Adelaide
                <br />
                · On the Stuart Highway (A87)
                <br />
                · Free parking available
                <br />
                · Coach parking available
              </div>
            </div>
            <div className="ct-phototile">
              <img src="/design/assets/contact-hero.jpg" alt="Umoona entrance" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to visit Umoona?</h2>
          <p className="u-ctacenter__sub">
            Call us, or book your underground tour and experience Coober Pedy's
            most iconic attraction.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/book-tour" className="u-btn u-btn--primary">
              Book Underground Tour
            </Link>
            <Link to="/underground-tours" className="u-btn u-btn--ghost">
              About the Tours
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
